// PlanComponents/CostAnalysisCard.js
import React, { useMemo } from 'react';
import { Typography, Table, TableBody, TableRow, TableCell, Box, LinearProgress, Chip, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import AdviceContentBox from '../../../components/AdviceContentBox';
import { toManYenLabel, calcDays, calcMonths } from './utils';

// 日本市場ベンチマーク（マイナビ中途採用状況調査2025年版）
const JAPAN_AVG_CPH = 289000; // 28.9万円
// SHRM/ISO 30414 グローバル平均
const GLOBAL_AVG_CPH = 700000; // 約70万円（$4,700）

/**
 * コスト分析カード
 * 科学的根拠: SHRM/ANSI Cost Per Hire標準 (2012), ISO 30414:2018
 */
export default function CostAnalysisCard({
  totalBudgetYen = 0,
  targetHires = 1,
  distribution,
  hiringMetrics,
  startDate,
  endDate,
  adv
}) {
  const theme = useTheme();

  const analysis = useMemo(() => {
    const cph = targetHires > 0 ? Math.floor(totalBudgetYen / targetHires) : 0;
    const days = calcDays(startDate, endDate);
    const months = calcMonths(startDate, endDate);
    const monthlyBudget = months > 0 ? Math.floor(totalBudgetYen / months) : 0;

    const mediaYen = distribution ? Math.floor(totalBudgetYen * (distribution.media / 100)) : 0;
    const agencyYen = distribution ? Math.floor(totalBudgetYen * (distribution.agency / 100)) : 0;
    const othersYen = distribution ? Math.floor(totalBudgetYen * (distribution.others / 100)) : 0;

    // 日本市場平均との比較（パーセント）
    const japanComparePercent = JAPAN_AVG_CPH > 0 ? Math.min(200, Math.round((cph / JAPAN_AVG_CPH) * 100)) : 0;

    // ステータス判定
    let cphStatus, cphColor;
    if (cph < JAPAN_AVG_CPH * 0.7) {
      cphStatus = '低コスト';
      cphColor = '#4caf50';
    } else if (cph <= JAPAN_AVG_CPH * 1.3) {
      cphStatus = '平均的';
      cphColor = '#ff9800';
    } else {
      cphStatus = '高コスト';
      cphColor = '#f44336';
    }

    return {
      cph, days, months, monthlyBudget,
      mediaYen, agencyYen, othersYen,
      japanComparePercent, cphStatus, cphColor
    };
  }, [totalBudgetYen, targetHires, distribution, startDate, endDate]);

  if (!totalBudgetYen || totalBudgetYen <= 0) {
    return (
      <CardItem title="コスト分析" adviceContent={adv?.costAnalysis ? <AdviceContentBox adviceObj={adv.costAnalysis} /> : null}>
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>予算が未設定です</Typography>
      </CardItem>
    );
  }

  const costItems = [
    { label: '媒体費', amount: analysis.mediaYen, color: '#ff7043', percent: distribution?.media },
    { label: '人材紹介費', amount: analysis.agencyYen, color: '#ab47bc', percent: distribution?.agency },
    { label: 'その他', amount: analysis.othersYen, color: '#42a5f5', percent: distribution?.others },
  ];

  return (
    <CardItem title="コスト分析" adviceContent={adv?.costAnalysis ? <AdviceContentBox adviceObj={adv.costAnalysis} /> : null}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* 一人当たり採用コスト */}
        <Box sx={{ p: 2, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textColors(theme).primary }}>
              一人当たり採用コスト (CPH)
            </Typography>
            <Tooltip title="計算式: (内部コスト + 外部コスト) ÷ 採用人数" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: textColors(theme).muted, cursor: 'help' }} />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: analysis.cphColor }}>
              {toManYenLabel(analysis.cph)}
            </Typography>
            <Chip label={analysis.cphStatus} size="small" sx={{ backgroundColor: analysis.cphColor, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }} />
          </Box>

          {/* 日本市場平均との比較バー */}
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                日本市場平均: {toManYenLabel(JAPAN_AVG_CPH)}
              </Typography>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                {analysis.japanComparePercent}%
              </Typography>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, analysis.japanComparePercent)}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(0,0,0,0.06)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: analysis.cphColor,
                    borderRadius: 5
                  }
                }}
              />
              {/* 100%マーカー（日本平均位置） */}
              <Box sx={{
                position: 'absolute',
                left: '50%',
                top: -2,
                width: 2,
                height: 14,
                backgroundColor: textColors(theme).muted,
                opacity: 0.6
              }} />
            </Box>
          </Box>
        </Box>

        {/* コスト内訳テーブル */}
        {distribution && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).primary, mb: 1 }}>
              コスト内訳
            </Typography>
            <Table size="small">
              <TableBody>
                {costItems.map(item => (
                  <TableRow key={item.label}>
                    <TableCell sx={{ border: 'none', py: 1, width: '35%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
                          {item.label} ({item.percent}%)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ border: 'none', py: 1, textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                        {toManYenLabel(item.amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ border: 'none', pt: 1.5, borderTop: `1px solid ${border(theme).light}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: textColors(theme).primary }}>
                      合計
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', pt: 1.5, textAlign: 'right', borderTop: `1px solid ${border(theme).light}` }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: textColors(theme).primary }}>
                      {toManYenLabel(totalBudgetYen)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}

        {/* 期間・月次予算 */}
        {analysis.days > 0 && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 140, p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}`, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>採用期間</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: textColors(theme).primary }}>
                {analysis.days}日間
              </Typography>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                (約{analysis.months}ヶ月)
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 140, p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}`, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>月次予算</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: textColors(theme).primary }}>
                {toManYenLabel(analysis.monthlyBudget)}
              </Typography>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>/月</Typography>
            </Box>
          </Box>
        )}

        {/* ベンチマーク比較（バックエンド計算値がある場合） */}
        {hiringMetrics?.benchmarkComparison && (
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip
                label={hiringMetrics.benchmarkComparison.status}
                size="small"
                sx={{
                  backgroundColor: hiringMetrics.benchmarkComparison.status === '効率的' ? '#4caf50'
                    : hiringMetrics.benchmarkComparison.status === '要注意' ? '#f44336' : '#ff9800',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
              {hiringMetrics.benchmarkComparison.detail}
            </Typography>
          </Box>
        )}

        {/* 出典 */}
        <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic', mt: 0.5 }}>
          ※ 日本市場平均値・グローバル平均値は公的統計および業界調査に基づく
        </Typography>
      </Box>
    </CardItem>
  );
}
